/**
 * Analytics event dictionary (Phase 1).
 * Screens and contexts must use these names — do not invent ad-hoc event strings.
 */

export const ANALYTICS_EVENTS = {
  APP_OPENED: 'app_opened',
  INSTALL_ATTRIBUTED: 'install_attributed',

  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_COMPLETED: 'registration_completed',
  LOGIN_COMPLETED: 'login_completed',
  LOGOUT: 'logout',

  BASIC_INFO_COMPLETED: 'basic_info_completed',
  PROFILE_PHOTO_UPLOADED: 'profile_photo_uploaded',
  PROFILE_PHOTO_SKIPPED: 'profile_photo_skipped',
  ONBOARDING_SURVEY_STARTED: 'onboarding_survey_started',
  ONBOARDING_SURVEY_SKIPPED: 'onboarding_survey_skipped',
  ONBOARDING_SURVEY_COMPLETED: 'onboarding_survey_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  ACTIVATION_FIRST_FEED_VIEWED: 'activation_first_feed_viewed',
  ACTIVATION_FIRST_POST_CREATED: 'activation_first_post_created',
  ACTIVATION_FIRST_WEIGHT_LOGGED: 'activation_first_weight_logged',
  ACTIVATION_FIRST_ROBI_MESSAGE: 'activation_first_robi_message',
  ACTIVATION_FIRST_LIKE: 'activation_first_like',
  ACTIVATION_FIRST_COMMENT: 'activation_first_comment',
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AuthMethod = 'email' | 'google' | 'apple' | 'facebook';

/** AppsFlyer standard / custom event names for dual-dispatch. */
export const APPSFLYER_EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [ANALYTICS_EVENTS.REGISTRATION_COMPLETED]: 'af_complete_registration',
  [ANALYTICS_EVENTS.LOGIN_COMPLETED]: 'af_login',
  [ANALYTICS_EVENTS.BASIC_INFO_COMPLETED]: 'basic_info_completed',
  [ANALYTICS_EVENTS.ONBOARDING_COMPLETED]: 'onboarding_completed',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_FEED_VIEWED]:
    'activation_first_feed_viewed',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED]:
    'activation_first_post_created',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_WEIGHT_LOGGED]:
    'activation_first_weight_logged',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE]:
    'activation_first_robi_message',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE]: 'activation_first_like',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_COMMENT]: 'activation_first_comment',
};

/**
 * Meta App Events (Events Manager).
 * Standard names use fb_mobile_* so Meta can optimize campaigns.
 */
export const META_EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [ANALYTICS_EVENTS.REGISTRATION_COMPLETED]: 'fb_mobile_complete_registration',
  [ANALYTICS_EVENTS.LOGIN_COMPLETED]: 'login',
  [ANALYTICS_EVENTS.BASIC_INFO_COMPLETED]: 'basic_info_completed',
  [ANALYTICS_EVENTS.ONBOARDING_COMPLETED]: 'fb_mobile_tutorial_completion',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_FEED_VIEWED]: 'fb_mobile_content_view',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED]:
    'activation_first_post_created',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_WEIGHT_LOGGED]:
    'activation_first_weight_logged',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE]:
    'activation_first_robi_message',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE]: 'activation_first_like',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_COMMENT]: 'activation_first_comment',
};

/**
 * TikTok App Events (Events Manager).
 * Values match TikTokEventName where a standard event exists.
 */
export const TIKTOK_EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [ANALYTICS_EVENTS.REGISTRATION_COMPLETED]: 'Registration',
  [ANALYTICS_EVENTS.LOGIN_COMPLETED]: 'Login',
  [ANALYTICS_EVENTS.BASIC_INFO_COMPLETED]: 'basic_info_completed',
  [ANALYTICS_EVENTS.ONBOARDING_COMPLETED]: 'CompleteTutorial',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_FEED_VIEWED]: 'VIEW_CONTENT',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED]:
    'activation_first_post_created',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_WEIGHT_LOGGED]:
    'activation_first_weight_logged',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE]:
    'activation_first_robi_message',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE]: 'activation_first_like',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_COMMENT]: 'activation_first_comment',
};

/** Firebase / GA4 event names (Google Ads conversions). */
export const FIREBASE_EVENT_MAP: Partial<Record<AnalyticsEventName, string>> = {
  [ANALYTICS_EVENTS.REGISTRATION_COMPLETED]: 'sign_up',
  [ANALYTICS_EVENTS.LOGIN_COMPLETED]: 'login',
  [ANALYTICS_EVENTS.BASIC_INFO_COMPLETED]: 'basic_info_completed',
  [ANALYTICS_EVENTS.ONBOARDING_COMPLETED]: 'tutorial_complete',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_FEED_VIEWED]:
    'activation_first_feed_viewed',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED]:
    'activation_first_post_created',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_WEIGHT_LOGGED]:
    'activation_first_weight_logged',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE]:
    'activation_first_robi_message',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE]: 'activation_first_like',
  [ANALYTICS_EVENTS.ACTIVATION_FIRST_COMMENT]: 'activation_first_comment',
};

/** Standard TikTok events that must use trackEvent, not trackCustomEvent. */
export const TIKTOK_STANDARD_EVENTS = new Set<string>([
  'Registration',
  'Login',
  'CompleteTutorial',
]);

/** TikTok content events that must use trackContentEvent. */
export const TIKTOK_CONTENT_EVENTS = new Set<string>(['VIEW_CONTENT']);

export type AnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined
>;
